/**
 * External Imports
 */
import { FC, useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import { useParams } from 'react-router-dom';
/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import { RootState } from '../../../../redux/store';
import CensusPdfTableFile from './PdfTable/patient/PatientCensusPdfTableFile';
import { logReportsClick } from '../../../../utils/analyticsHelpers';
import EmployeeRoundsPdfTableFile from './PdfTable/employee/EmployeeRoundsPdfTableFile';
import PatientRoundsPdfTableFile from './PdfTable/patient/PatientRoundsPdfTableFile';
import { clearDataToDownload } from '../../../../redux/actions';
/**
 * Style Imports
 */
import { StyledExportCsvBtn } from '../../../searchHeader/exportCsvBtn/ExportCsvBtn.styles';

interface DownloadPdfBtnProps {
  handleClickDownloadPDF?: () => void;
  dataToDownload: any;
  isLoading: boolean;
}

const DownloadPdfBtn: FC<DownloadPdfBtnProps> = ({
  handleClickDownloadPDF,
  dataToDownload,
  isLoading
}) => {
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();
  const dispatch = useDispatch();

  const handleClickDownloadPDFExport = useCallback(() => {
    switch (view) {
      case 'employee/rounds':
      case 'employee/compliance':
      case 'employee/frequency':
      case 'patient/compliance':
      case 'patient/rounds':
      case 'patient/census':
        logTrackingEvent(
          logReportsClick(view?.replace('/', '-'), 'download-pdf')
        );
        break;
      default:
        console.log('No view for event');
    }
    if (handleClickDownloadPDF) handleClickDownloadPDF();
  }, [logTrackingEvent, logReportsClick, handleClickDownloadPDF]);

  const generateAndDownloadPdf = async () => {
    let asPdf;
    let pdfFileName;
    if (view === 'employee/rounds') {
      asPdf = pdf(<EmployeeRoundsPdfTableFile data={dataToDownload} />);
      pdfFileName = 'employee-rounds';
    } else if (view === 'patient/rounds') {
      asPdf = pdf(<PatientRoundsPdfTableFile data={dataToDownload} />);
      pdfFileName = 'patient-rounds';
    } else {
      asPdf = pdf(<CensusPdfTableFile data={dataToDownload} />);
      pdfFileName = 'patient-census';
    }
    const blob = await asPdf.toBlob();
    const downloadBlob = (name = 'file.txt') => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;

      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
      document.body.removeChild(link);
    };

    return downloadBlob(
      `${pdfFileName}-report-${new Date().toISOString()}.pdf`
    );
  };

  useEffect(() => {
    if (dataToDownload.length > 0) {
      generateAndDownloadPdf();
      dispatch(clearDataToDownload());
    }
  }, [dataToDownload]);

  return (
    <StyledExportCsvBtn
      id="Download-Pdf-Button"
      className="w-100 me-1"
      color="blue"
      icon="get_app"
      kind="tertiary"
      size="small"
      style={{ minWidth: 'fit-content' }}
      onClick={e => {
        e.preventDefault();
        handleClickDownloadPDFExport();
      }}
      disabled={isLoading}
    >
      Export PDF
    </StyledExportCsvBtn>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    dataToDownload: state.AdminReducer.dataToDownload,
    isLoading: state.AdminReducer.isLoadingCsvExport
  };
};

export default connect(mapReduxStateToProps)(DownloadPdfBtn);
